import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useIntl, FormattedMessage } from 'react-intl';
import { Link } from '@bibliocommons/base-links';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import './EventDateTime.scss';

function parseDateTime(dateTimeStr) {
  const [dateStr, timeStr] = (dateTimeStr || '').split('T');
  return {
    date: dateStr ? moment(dateTimeStr).format('LL') : null,
    time: timeStr ? moment(dateTimeStr).format('LT') : null
  };
}

export default function EventDateTime({ start, end, includeYear, dateLink, trackEventLinkClick }) {
  const intl = useIntl();

  const { date: startDate, time: startTime } = parseDateTime(start);
  const { date: endDate, time: endTime } = parseDateTime(end);
  const isMultiDay = startDate !== endDate;
  const isAllDay = !startTime && !endTime;
  const eventStartTime = startTime?.replace(/\s+/g, '').toLowerCase();
  const eventEndTime = endTime?.replace(/\s+/g, '').toLowerCase();

  function formatDate(date) {
    if (includeYear) {
      return intl.formatDate(moment.utc(date).toDate(), {
        year: 'numeric',
        weekday: 'long',
        month: 'long',
        day: '2-digit'
      });
    }
    return intl.formatDate(moment.utc(date).toDate(), {
      weekday: 'long',
      month: 'long',
      day: '2-digit'
    });
  }

  const eventDateText = isMultiDay
    ? `${formatDate(start)} ${intl.formatMessage({ id: 'to' })} ${formatDate(end)}`
    : formatDate(start);

  function renderEventDate() {
    if (!isMultiDay && !startDate) {
      return null;
    }

    const eventDate = (
      <>
        <span aria-hidden>{eventDateText}</span>
        <ScreenReaderMessage>
          {isMultiDay ? (
            <FormattedMessage id="sr_event_multi_dates" values={{ startDate, endDate }} />
          ) : (
            <FormattedMessage id="sr_event_date" values={{ startDate }} />
          )}
        </ScreenReaderMessage>
      </>
    );
    const eventTime = !isAllDay && (
      <>
        <span className="event-time" aria-hidden>
          {eventStartTime}&#8211;{eventEndTime}
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="sr_event_times" values={{ startTime: eventStartTime, endTime: eventEndTime }} />
        </ScreenReaderMessage>
      </>
    );

    if (isAllDay) {
      return (
        <span className="cp-event-date-time">
          <FormattedMessage id="all_day" />
          <span className="date-join">, </span>
          {eventDate}
        </span>
      );
    }

    return (
      <span className="cp-event-date-time">
        {dateLink ? (
          <>
            <Link href={dateLink} dataKey="event-link" handleClick={() => trackEventLinkClick?.(eventDateText)}>
              {eventDate}
            </Link>
            <span className="date-join">, </span>
            {eventTime}
          </>
        ) : (
          <>
            {eventDate}
            <span className="date-join">, </span>
            {eventTime}
          </>
        )}
      </span>
    );
  }

  return renderEventDate();
}

EventDateTime.propTypes = {
  start: PropTypes.string,
  end: PropTypes.string,
  includeYear: PropTypes.bool,
  dateLink: PropTypes.string,
  trackEventLinkClick: PropTypes.func
};
